import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import agentApiService from '../../services/agentApiService';

const SearchHistory = () => {
  const [searchHistory, setSearchHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('history'); // 'history' (paginated), 'recent', 'stats'
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false,
    isFirst: true,
    isLast: false
  });
  
  // Sorting state
  const [sortBy, setSortBy] = useState('searchDate');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    fetchSearchHistory();
  }, [activeTab, pagination.currentPage, sortBy, sortDir, pageSize]);

  const fetchSearchHistory = async () => {
    try {
      setLoading(true);
      let data;
      
      switch (activeTab) {
        case 'recent':
          data = await agentApiService.getRecentSearchHistory();
          setSearchHistory(data.searchHistory || []);
          break;
        case 'stats':
          data = await agentApiService.getSearchHistoryStats();
          setStats(data.stats || {});
          break;
        default: // 'history' - always use pagination
          data = await agentApiService.getPaginatedSearchHistory(
            pagination.currentPage, 
            pageSize, 
            sortBy, 
            sortDir
          );
          setSearchHistory(data.searchHistory || []);
          setPagination(data.pagination || {});
      }
    } catch (error) {
      console.error('Error fetching search history:', error);
      toast.error('Failed to load search history: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setPagination(prev => ({ ...prev, currentPage: 0 }));
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPagination(prev => ({ ...prev, currentPage: 0 }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return <i className="fas fa-sort text-gray-400"></i>;
    return sortDir === 'asc' 
      ? <i className="fas fa-sort-up text-blue-500"></i>
      : <i className="fas fa-sort-down text-blue-500"></i>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">📜 Search History</h2>
        <p className="text-gray-600">View and manage your hotel search history</p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('history')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Search History
            </button>
            <button
              onClick={() => setActiveTab('recent')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'recent'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Recent (Last 5)
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'stats'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Statistics
            </button>
          </nav>
        </div>
      </div>

      {/* Statistics Tab */}
      {activeTab === 'stats' && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                <i className="fas fa-search text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Searches</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalSearches || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-600">
                <i className="fas fa-city text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Unique Cities</p>
                <p className="text-2xl font-bold text-gray-900">{stats.uniqueCities || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                <i className="fas fa-hotel text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Unique Hotels</p>
                <p className="text-2xl font-bold text-gray-900">{stats.uniqueHotels || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100 text-orange-600">
                <i className="fas fa-star text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Most Searched</p>
                <p className="text-lg font-bold text-gray-900">{stats.mostSearchedCity || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search History Table */}
      {(activeTab === 'history' || activeTab === 'recent') && (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">
                  {activeTab === 'recent' ? 'Recent Searches (Last 5)' : 'Search History'}
                </h3>
                {activeTab === 'history' && pagination.totalElements > 0 && (
                  <p className="text-sm opacity-90 mt-1">
                    Total {pagination.totalElements.toLocaleString()} searches found
                  </p>
                )}
              </div>
              
              {/* Page Size Selector for History Tab */}
              {activeTab === 'history' && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm">Show:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                    className="bg-white text-gray-900 rounded px-2 py-1 text-sm"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm">per page</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="p-6">
            {searchHistory?.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sr.No
                        </th>
                        <th 
                          className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                            activeTab === 'history' ? 'cursor-pointer hover:bg-gray-100' : ''
                          }`}
                          onClick={() => activeTab === 'history' && handleSortChange('city')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>City / Location</span>
                            {activeTab === 'history' && getSortIcon('city')}
                          </div>
                        </th>
                        <th 
                          className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                            activeTab === 'history' ? 'cursor-pointer hover:bg-gray-100' : ''
                          }`}
                          onClick={() => activeTab === 'history' && handleSortChange('searchDate')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Search Date</span>
                            {activeTab === 'history' && getSortIcon('searchDate')}
                          </div>
                        </th>
                        <th 
                          className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                            activeTab === 'history' ? 'cursor-pointer hover:bg-gray-100' : ''
                          }`}
                          onClick={() => activeTab === 'history' && handleSortChange('checkInDate')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Check-In</span>
                            {activeTab === 'history' && getSortIcon('checkInDate')}
                          </div>
                        </th>
                        <th 
                          className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                            activeTab === 'history' ? 'cursor-pointer hover:bg-gray-100' : ''
                          }`}
                          onClick={() => activeTab === 'history' && handleSortChange('checkOutDate')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Check-Out</span>
                            {activeTab === 'history' && getSortIcon('checkOutDate')}
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Room Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Pax
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {searchHistory.map((history, index) => {
                        const destination = history.destination || history.city || '-';
                        const state = history.state || '';
                        const country = history.country || '';
                        const fullLocation = [destination !== '-' ? destination : '', state, country].filter(Boolean).join(', ') || destination;
                        const stateCountry = [state, country].filter(Boolean).join(', ');

                        // Calculate passenger information
                        const calculatePassengerInfo = (history) => {
                          let totalAdults = 0;
                          let totalChildren = 0;
                          let totalRooms = 0;
                          let roomDetails = [];

                          if (history.rooms && Array.isArray(history.rooms)) {
                            // New format with room allocations
                            totalRooms = history.rooms.length;
                            history.rooms.forEach((room, roomIndex) => {
                              const adults = room.adults || room.adultCount || 0;
                              const children = room.children || room.childCount || 0;
                              totalAdults += adults;
                              totalChildren += children;
                              roomDetails.push({
                                roomNumber: roomIndex + 1,
                                adults,
                                children,
                                total: adults + children
                              });
                            });
                          } else {
                            // No rooms data available - use defaults
                            totalAdults = 1;
                            totalChildren = 0;
                            totalRooms = 1;
                            roomDetails.push({
                              roomNumber: 1,
                              adults: 1,
                              children: 0,
                              total: 1
                            });
                          }

                          return {
                            totalAdults,
                            totalChildren,
                            totalRooms,
                            totalGuests: totalAdults + totalChildren,
                            roomDetails
                          };
                        };

                        const passengerInfo = calculatePassengerInfo(history);

                        return (
                          <tr key={history.searchId || index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {activeTab === 'history' 
                                ? (pagination.currentPage * pageSize) + index + 1
                                : index + 1
                              }
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs" title={fullLocation}>
                              <div className="flex items-start">
                                <i className="fas fa-map-marker-alt text-red-500 mr-2 mt-0.5 flex-shrink-0"></i>
                                <div className="min-w-0">
                                  <div className="font-medium text-gray-900 truncate">{destination}</div>
                                  {stateCountry && (
                                    <div className="text-xs text-gray-500 truncate">{stateCountry}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(history.searchDate)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(history.checkInDate)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(history.checkOutDate)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              <div className="space-y-1">
                                {passengerInfo.roomDetails.length > 0 ? (
                                  passengerInfo.roomDetails.map((room, roomIndex) => (
                                    <div key={roomIndex} className="text-xs">
                                      <span className="font-medium">R{room.roomNumber}:</span>
                                      <span className="ml-1">
                                        {room.adults}A{room.children > 0 ? `, ${room.children}C` : ''}
                                      </span>
                                    </div>
                                  ))
                                ) : (
                                  <div className="text-xs text-gray-400">
                                    {passengerInfo.totalRooms} Room{passengerInfo.totalRooms > 1 ? 's' : ''}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="text-sm font-medium text-gray-900">
                                {(() => {
                                  // Generate compact format: 2R-(2A2C)-(2A2C)
                                  let compactFormat = `${passengerInfo.totalRooms}R`;
                                  if (passengerInfo.roomDetails.length > 0) {
                                    const roomStrings = passengerInfo.roomDetails.map(room => 
                                      `(${room.adults}A${room.children > 0 ? `${room.children}C` : ''})`
                                    );
                                    compactFormat += `-${roomStrings.join('-')}`;
                                  }
                                  return compactFormat;
                                })()}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls for History Tab */}
                {activeTab === 'history' && pagination.totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-700">
                      <span>
                        Showing {(pagination.currentPage * pageSize) + 1} to{' '}
                        {Math.min((pagination.currentPage + 1) * pageSize, pagination.totalElements)} of{' '}
                        {pagination.totalElements} results
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(0)}
                        disabled={pagination.isFirst}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        First
                      </button>
                      
                      <button
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={!pagination.hasPrevious}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      
                      <span className="px-3 py-2 text-sm font-medium text-gray-700">
                        Page {pagination.currentPage + 1} of {pagination.totalPages}
                      </span>
                      
                      <button
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={!pagination.hasNext}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                      
                      <button
                        onClick={() => handlePageChange(pagination.totalPages - 1)}
                        disabled={pagination.isLast}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Last
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <i className="fas fa-history text-4xl mb-4"></i>
                <p className="text-lg font-medium">No search history found</p>
                <p className="text-sm">Your hotel searches will appear here</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchHistory;